import { useState, useRef, useEffect, useLayoutEffect } from "react";

/**
 * useReportsLogic
 * A custom hook that encapsulates all state, refs, effects and handlers
 * used for managing the interactive behavior of the reports page.
 */

export function useReportsLogic(initialFile, parentHeight, fullScreen) { 
  // Create separate internal height states to avoid any cross-contamination
  const [calculatedHeight, setCalculatedHeight] = useState(() => {
    // Initial value based on the mode
    if (fullScreen) {
      return window.innerHeight - 70; // modalHeaderHeight
    } else {
      return typeof parentHeight === "number" && isFinite(parentHeight) && parentHeight >= 200
        ? parentHeight
        : 500; // Safe default
    }
  });

  // Keep track of the last valid height value for each mode separately
  const lastNormalHeight = useRef(
    typeof parentHeight === "number" && isFinite(parentHeight) && parentHeight >= 200
      ? parentHeight
      : 500
  );
  const lastFullscreenHeight = useRef(window.innerHeight - 70); // modalHeaderHeight

  const [selectedFile, setSelectedFile] = useState(null);
  const [treeWidth, setTreeWidth] = useState(300);
  const [isDraggingToolbar, setIsDraggingToolbar] = useState(false);
  const [isResizingColumn, setIsResizingColumn] = useState(false);
  const [dragPos, setDragPos] = useState({ x: window.innerWidth / 2, y: null });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizeOffset, setResizeOffset] = useState(0);
  const [pdfError, setPdfError] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState(null);
  const [viewedFiles, setViewedFiles] = useState(() => new Set());
  const [isDocumentLoading, setIsDocumentLoading] = useState(false);
  const [pageSize, setPageSize] = useState({ width: 210, height: 297 }); // fallback A4
  const [fileZoomLevels, setFileZoomLevels] = useState({});
  const useFitToPage = useRef(true);

  const pageRef = useRef();
  const pdfContainerRef = useRef();
  const containerRef = useRef();
  const dimensionsInitialized = useRef(false);
  const lastHeightUpdateSource = useRef("init");

  // Use a layout effect to update height before render
  useLayoutEffect(() => {
    console.log(`Height update (source: ${lastHeightUpdateSource.current})`);
    console.log(`  fullScreen: ${fullScreen}`);
    console.log(`  parentHeight: ${parentHeight}`);
    console.log(`  lastNormalHeight: ${lastNormalHeight.current}`);
    console.log(`  lastFullscreenHeight: ${lastFullscreenHeight.current}`);

    if (fullScreen) {
      // In fullscreen - use window height minus header and store it
      const fsHeight = window.innerHeight - 70; // modalHeaderHeight
      lastFullscreenHeight.current = fsHeight;
      setCalculatedHeight(fsHeight);
      console.log(`  → Setting fullscreen height: ${fsHeight}`);
      lastHeightUpdateSource.current = "fullscreen";
    } else {
      // In normal mode - use parent height if valid
      if (typeof parentHeight === "number" && isFinite(parentHeight) && parentHeight >= 200) {
        lastNormalHeight.current = parentHeight; // Store this valid height
        setCalculatedHeight(parentHeight);
        console.log(`  → Setting normal height from parent: ${parentHeight}`);
        lastHeightUpdateSource.current = "parent";
      } else {
        // If no valid parent height, use the last stored normal height
        setCalculatedHeight(lastNormalHeight.current);
        console.log(`  → Using stored normal height: ${lastNormalHeight.current}`);
        lastHeightUpdateSource.current = "stored";
      }
    }
  }, [fullScreen, parentHeight]);

  // Initialize when component mounts
  useEffect(() => {
    const updateDimensions = () => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      console.log("Container dimensions:", rect.width, "x", rect.height);

      // Set toolbar to a sensible default position
      const defaultX = Math.max(150, Math.min(rect.width / 2, rect.width - 150));
      const defaultY = Math.max(50, Math.min(rect.height - 50, rect.height - 40));

      setDragPos({
        x: defaultX,
        y: defaultY
      });

      dimensionsInitialized.current = true;
    };

    // Set initial dimensions with a small delay to ensure DOM is ready
    const timeout = setTimeout(updateDimensions, 100);
    return () => clearTimeout(timeout);
  }, []);

  // Update toolbar position when container dimensions change due to fullscreen toggle
  useEffect(() => {
    if (!containerRef.current || isDraggingToolbar) return;

    const updateToolbarPosition = () => {
      const rect = containerRef.current.getBoundingClientRect();

      // Only adjust if we have meaningful dimensions
      if (rect.width < 50 || rect.height < 50) return;

      // Preserve horizontal position if possible, but ensure it's within bounds
      // For vertical, keep a fixed distance from bottom in both modes
      const newX = Math.min(rect.width - 150, Math.max(150, dragPos.x || rect.width / 2));
      const newY = Math.min(rect.height - 40, Math.max(50, rect.height - 40));

      console.log("Updating toolbar position:", {
        container: { width: rect.width, height: rect.height },
        toolbar: { x: newX, y: newY }
      });

      setDragPos({ x: newX, y: newY });
    };

    // Small delay to ensure the container has been resized first
    const timeout = setTimeout(updateToolbarPosition, 50);
    return () => clearTimeout(timeout);
  }, [fullScreen, calculatedHeight, isDraggingToolbar]);

  // Update toolbar position when file changes
  useEffect(() => {
    if (containerRef.current && selectedFile && !isDraggingToolbar) {
      const rect = containerRef.current.getBoundingClientRect();
      // Center horizontally, fixed distance from bottom
      setDragPos({
        x: rect.width / 2,
        y: rect.height - 40
      });
    }
  }, [selectedFile, isDraggingToolbar]);

  useEffect(() => {
    if (initialFile) {
      setSelectedFile(initialFile);
    }
  }, [initialFile]);

  useEffect(() => {
    const container = pdfContainerRef.current;
    if (!container || !isPanning) return;

    const handleMouseDown = (e) => {
      setPanStart({ x: e.clientX, y: e.clientY, scrollLeft: container.scrollLeft, scrollTop: container.scrollTop });
    };

    const handleMouseMove = (e) => {
      if (!panStart) return;
      const dx = e.clientX - panStart.x;
      const dy = e.clientY - panStart.y;
      container.scrollLeft = panStart.scrollLeft - dx;
      container.scrollTop = panStart.scrollTop - dy;
    };

    const handleMouseUp = () => setPanStart(null);

    container.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      container.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isPanning, panStart]);

  const handleSelectFile = (file) => {
    const normalized = typeof file === "string" ? { url: file } : file;
    const fileKey = normalized.url ?? normalized.name;

    const mode = fullScreen ? "fullscreen" : "normal";
    const savedZoom = fileZoomLevels[fileKey]?.[mode];
    useFitToPage.current = savedZoom === undefined;

    if (savedZoom !== undefined) {
      setScale(savedZoom);
    } else {
      setScale(1.0);
    }

    // Update the state directly instead of using a ref
    setSelectedFile(normalized);
    setPdfError(null);
    setPageNumber(1);
    setNumPages(null);
    setIsDocumentLoading(true);
  };

  const onDocumentLoadSuccess = async ({ numPages, pdf }) => {
    try {
      const firstPage = await pdf.getPage(1);
      const [x1, y1, x2, y2] = firstPage.view;
      const widthPt = x2 - x1;
      const heightPt = y2 - y1;

      const widthMm = (widthPt / 72) * 25.4;
      const heightMm = (heightPt / 72) * 25.4;
      setPageSize({ width: widthMm, height: heightMm });
      console.log("PDF page size (mm):", widthMm, "x", heightMm);
    } catch (err) {
      console.warn("Kunde inte läsa PDF-sidstorlek, använder fallback A4.");
    }

    setIsDocumentLoading(false);

    if (!numPages || isNaN(numPages)) {
      console.error("Ogiltigt antal sidor:", numPages);
      setPdfError("Kunde inte ladda PDF");
      return;
    }

    setNumPages(numPages);

    // Use selectedFile state directly instead of ref
    const fileName = selectedFile?.url ?? selectedFile?.name ?? "";

    const mode = fullScreen ? "fullscreen" : "normal";
    const viewedKey = `${fileName}::${mode}`;
    if (!viewedFiles.has(viewedKey) && useFitToPage.current) {
      // Vi försöker först efter lite fördröjning
      const tryFitToPage = () => {
        if (!pageRef.current || !pdfContainerRef.current) return false;

        const pageBox = pageRef.current.getBoundingClientRect();
        const containerBox = pdfContainerRef.current.getBoundingClientRect();

        const pageReady = pageBox.width > 0 && pageBox.height > 0;
        const containerReady = containerBox.width > 0 && containerBox.height > 0;

        if (pageReady && containerReady) {
          const scaleW = containerBox.width / pageBox.width;
          const scaleH = containerBox.height / pageBox.height;
          const optimalScale = Math.min(scaleW, scaleH) * 0.9;

          setScale(optimalScale);

          const mode = fullScreen ? "fullscreen" : "normal";
          setFileZoomLevels(prev => ({
            ...prev,
            [fileName]: {
              ...prev[fileName],
              [mode]: optimalScale
            }
          }));
          setViewedFiles(prev => new Set(prev).add(viewedKey));
          console.log(`Fit to page applied to "${fileName}" with scale:`, optimalScale.toFixed(2));
          return true;
        }

        return false;
      };

      let attempts = 0;
      const maxAttempts = 20;

      const waitUntilReady = () => {
        if (tryFitToPage()) return;
        if (attempts++ > maxAttempts) {
          console.warn(`Fit to page failed for "${fileName}" – page never rendered`);
          return;
        }
        requestAnimationFrame(waitUntilReady);
      };

      setTimeout(waitUntilReady, 50); // liten initial delay
    }
  };

  const zoomIn = () => {
    const mode = fullScreen ? "fullscreen" : "normal";
    setScale(prev => {
      const newScale = Math.min(prev + 0.2, 3);
      const fileKey = selectedFile?.url ?? selectedFile?.name ?? "";
      setFileZoomLevels(prevZooms => ({
        ...prevZooms,
        [fileKey]: {
          ...prevZooms[fileKey],
          [mode]: newScale
        }
      }));
      return newScale;
    });

  };

  const zoomOut = () => {
    const mode = fullScreen ? "fullscreen" : "normal";
    setScale(prev => {
      const newScale = Math.min(prev - 0.2, 3);
      const fileKey = selectedFile?.url ?? selectedFile?.name ?? "";
      setFileZoomLevels(prevZooms => ({
        ...prevZooms,
        [fileKey]: {
          ...prevZooms[fileKey],
          [mode]: newScale
        }
      }));
      return newScale;
    });
  };

  const goToPrevPage = () => setPageNumber(prev => Math.max(prev - 1, 1));
  const goToNextPage = () => setPageNumber(prev => Math.min(prev + 1, numPages));

  const startToolbarDrag = (e) => {
    e.stopPropagation();
    setIsDraggingToolbar(true);
    setDragOffset({ x: e.clientX - dragPos.x, y: e.clientY - dragPos.y });
  };

  const doDrag = (e) => {
    if (isDraggingToolbar) {
      // Calculate new position and enforce boundaries
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;

      // Get container dimensions for boundary checking
      const rect = containerRef.current?.getBoundingClientRect() || { width: window.innerWidth, height: window.innerHeight };

      // Enforce boundaries - keep toolbar within container with margins
      const boundedX = Math.min(Math.max(newX, 20), rect.width - 20);
      const boundedY = Math.min(Math.max(newY, 20), rect.height - 20);

      setDragPos({ x: boundedX, y: boundedY });
    } else if (isResizingColumn) {
      const newWidth = Math.min(Math.max(e.clientX - resizeOffset, 150), 600);
      setTreeWidth(newWidth);
    }
  };

  const endDrag = () => {
    setIsDraggingToolbar(false);
    setIsResizingColumn(false);
  };

  return {
    calculatedHeight,
    treeWidth,
    selectedFile,
    numPages,
    pageNumber,
    scale,
    dragPos,
    isPanning,
    pdfError,
    isDocumentLoading,
    setIsDocumentLoading,
    setResizeOffset,
    setIsResizingColumn,
    setIsPanning,
    setPdfError,
    containerRef,
    pdfContainerRef,
    pageRef,
    handleSelectFile,
    onDocumentLoadSuccess,
    goToPrevPage,
    goToNextPage,
    zoomIn,
    zoomOut,
    doDrag,
    endDrag,
    startToolbarDrag,
    pageSize,
  };
}